import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { setToken, getToken, getCurrentUser } from '../lib/auth.ts';
import { Input } from '../components/ui/Input.tsx';
import { Button } from '../components/ui/Button.tsx';
import { MapLogo } from '../components/ui/MapLogo.tsx';

/**
 * Extracts the tenant slug from the current hostname.
 *
 * When VITE_TENANT_BASE_DOMAIN is set (e.g. "llt-map.justbit.click"):
 *  - "tenant1.llt-map.justbit.click"  → "tenant1"
 *  - "admin.llt-map.justbit.click"    → "admin"  (super-admin mode)
 *  - "llt-map.justbit.click"          → null     (base domain, no tenant)
 *  - "d32zdbnfb0vqnx.cloudfront.net"  → null     (direct CF URL)
 *  - "localhost"                       → null     (local dev)
 *
 * Fallback (no env var): detects slug when hostname has ≥3 parts and
 * the first part is not a CloudFront hex subdomain.
 */
function extractTenantSlug(): string | null {
  // ?tenant= query param — dev shortcut to test a tenant without subdomain config
  const querySlug = new URLSearchParams(window.location.search).get('tenant');
  if (querySlug) return querySlug;

  const hostname = window.location.hostname;
  const baseDomain = (import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined)?.trim();

  if (baseDomain) {
    // Slug is a single label prepended to the base domain: <slug>.<baseDomain>
    if (!hostname.endsWith('.' + baseDomain)) return null;
    const slug = hostname.slice(0, -(baseDomain.length + 1));
    // Reject nested subdomains (e.g. "a.b.base.domain") or empty
    if (!slug || slug.includes('.')) return null;
    return slug;
  }

  // Fallback for local dev (no VITE_TENANT_BASE_DOMAIN set)
  const parts = hostname.split('.');
  if (parts.length < 3) return null;
  const candidate = parts[0];
  if (/^[a-z0-9]{12,}$/.test(candidate)) return null;
  return candidate;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const tenantSlug = extractTenantSlug();
  const isSuperAdminDomain = tenantSlug === 'admin';
  // On a tenant subdomain we know the tenant from the URL — no manual input needed.
  const hasTenantFromUrl = tenantSlug !== null && !isSuperAdminDomain;

  // If a valid (non-expired) token already exists, skip the login page entirely.
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const user = getCurrentUser();
    if (user?.exp && user.exp * 1000 < Date.now()) return; // expired — stay on login
    navigate(user?.role === 'super_admin' ? '/admin/tenants' : '/', { replace: true });
  }, [navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const body: Record<string, string> = { email, password };
      if (hasTenantFromUrl) {
        // Pass the slug extracted from the URL — backend resolves it to tenantId.
        body.tenantSlug = tenantSlug!;
      }
      const res = await api.post<{ token: string }>('/auth/login', body);
      setToken(res.data.token);
      const user = getCurrentUser();
      navigate(user?.role === 'super_admin' ? '/admin/tenants' : '/', { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Invalid credentials';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / wordmark */}
        <div className="mb-8 flex flex-col items-center">
          <MapLogo variant="full" theme="dark" className="mb-1" />
        </div>

        {/* Card */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-sm font-medium text-zinc-900 mb-1">Sign in to your account</h2>
          {hasTenantFromUrl && (
            <p className="text-xs text-zinc-400 mb-5">
              Workspace: <span className="font-medium text-zinc-600">{tenantSlug}</span>
            </p>
          )}
          {!hasTenantFromUrl && <div className="mb-5" />}

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2.5">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full mt-1" loading={loading}>
              Sign in
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-zinc-400">MAP v0.1 — internal tool</p>
      </div>
    </div>
  );
}
